import { TradeInCsl } from "../../TradeInCsl";
import { TradeInAemNavStep, TradeInAemSearchType } from "../../../utils/constants";
import { fetchFindVehicle, handleVehicleFetch } from "../../../utils/utils";
import { tradeInAemSetQuote, tradeInAemSetQuoteFailed } from "../../../redux/actions/TradeInAemVehicleValueActions";
import type {
    TradeInAemEHubPayload,
    TradeInAemExtraVehicleDetailsPdfBaseType,
    TradeInAemFetchVehicleResultsReturnType,
    TradeInAemVehicleExtraInfoType,
    TradeInAemVehicleNameType,
    TradeInAemVehicleType,
    TradeInAemVehicleValueType,
} from "../../../utils/types";
import type { TradeInAemStateType } from "../../../redux/store";
import { type TradeInAemLabelType, getTradeInAemLabel } from "../../../utils/labels";
import { formatNumber } from "../../../../common-deprecated/Globalize";
import type { DealerFinderSettingsType } from "../../../../common-deprecated/components/AemDealerFinder";

type SwAttribute = {
    id: string | number;
    name: string;
    unit: string;
    values?: (string | number)[];
    attributes?: SwAttribute[];
};

export type TradeInAemVehicleValuationTypeSw = {
    [key: string]: unknown; // There is other stuff in the response but it doesnt look useful for us.
    // eslint-disable-next-line camelcase
    private_valuation: {
        result: {
            price: number;
        };
    };
};

export type TradeInAemVehicleTypeSw = {
    brand: string;
    series: string;
    model: string;
    generation: string;
    name: string;
    chassis: string;
    engine: string;
    engineStartYear: number;
    engineEndYear: null | number;
    engineName: string;
    horsepower: number;
    modelYear: number;
    trimPackage: string;
    plate: string;
    vin: string;
    type: string;
    attributes: SwAttribute[];
    valuation: TradeInAemVehicleValuationTypeSw;

    // Vehicle detail fields.
    mileage?: string;

    // Extra info property - used to save some data which will be used for the PDF later
    tmeExtraInfo?: TradeInAemVehicleExtraInfoType;
};

// Specific properties which are only shown for TMI in the vehicle extra details in the PDF
export type TradeInAemExtraVehicleDetailsPdfSwSpecificType = {
    series: string;
    chassis: string;
    modelYear: number;
    engineStartYear: number;
    engineEndYear: number | null;
    trimPackage: string;
    driveline: string | null;
};

// Combination of base props + TGB specific props
type TradeInAemSwExtraVehicleDetailsPdfType = TradeInAemExtraVehicleDetailsPdfBaseType &
    TradeInAemExtraVehicleDetailsPdfSwSpecificType;

export class TradeInCslSw extends TradeInCsl {
    // ----------------------------------------------------------------------
    // Static properties
    // ----------------------------------------------------------------------
    enabledNavSteps = [
        TradeInAemNavStep.FindVehicle,
        TradeInAemNavStep.VehicleDetails,
        TradeInAemNavStep.ModelSelect,
        TradeInAemNavStep.DealerFinder,
        TradeInAemNavStep.ContactForm,
        TradeInAemNavStep.Summary,
    ];

    enableVin = false;

    enableLicensePlate = true;

    enableResolve = false;

    enablePreviouslySearchedVehicles = true;

    resolveKeyMap: Record<string, TradeInAemLabelType> = {};

    dealerFinderSettings: DealerFinderSettingsType = {};

    testPlates = [{ value: "FZJ55T", description: "" }];

    hideValuationValueOnSummary = true;

    // ----------------------------------------------------------------------
    // Selectors
    // ----------------------------------------------------------------------
    vehicleValueSelector(state: TradeInAemStateType): TradeInAemVehicleValueType | null {
        const valuation = state.tradeInAemVehicleValue.valuation as TradeInAemVehicleValuationTypeSw | null;
        return valuation ? { value: valuation.private_valuation.result.price, validityDate: "", quoteId: "" } : null;
    }

    vehiclePlateOrVinSelector(state: TradeInAemStateType): string {
        const { searchType } = state.tradeInAemFindVehicle;
        const vehicle = state.tradeInAemVehicle.vehicle as TradeInAemVehicleTypeSw;
        return searchType === TradeInAemSearchType.LicensePlate ? vehicle.plate : vehicle.vin;
    }

    vehicleInfoBlockSelector(state: TradeInAemStateType): { label: string; value: string }[] {
        const vehicle = state.tradeInAemVehicle.vehicle as TradeInAemVehicleTypeSw;
        if (!vehicle) return [];

        const { formItems } = state.tradeInAemVehicleDetails;
        const mileageValue = formatNumber(Number(formItems.mileage.value as string), state.commonSettings.culture.name);
        const mileageLabel = getTradeInAemLabel(state, "tradeInAemMileageFormat").replace("{value}", mileageValue);

        return [
            { label: getTradeInAemLabel(state, "tradeInAemVehicleDetailsLicensePlate"), value: vehicle.plate },
            { label: formItems.mileage.label, value: mileageLabel },
        ];
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    vehicleQuoteExpirySelector(state: TradeInAemStateType): string {
        return "";
    }

    // ----------------------------------------------------------------------
    // Non-selector functions
    // ----------------------------------------------------------------------
    getVehicleName(vehicle: TradeInAemVehicleType): TradeInAemVehicleNameType {
        const typedVehicle = vehicle as TradeInAemVehicleTypeSw;
        return {
            name: `${typedVehicle.brand} ${typedVehicle.model}`,
            description: typedVehicle.generation,
        };
    }

    getVehicleKey(vehicle: TradeInAemVehicleType): string {
        const typedVehicle = vehicle as TradeInAemVehicleTypeSw;
        return `${typedVehicle.vin}-${typedVehicle.plate}-${typedVehicle.name}`;
    }

    async fetchVehicleResults(value: string, type: TradeInAemSearchType): Promise<void> {
        const { dispatch, getState } = this.getStore();
        const { commonSettings, tradeInAemSettings } = getState();

        const fetchFn = async (): Promise<TradeInAemFetchVehicleResultsReturnType> => {
            const headers = this.getTradeInApiHeaders("GET");
            const response = await fetchFindVehicle(value, type, commonSettings, tradeInAemSettings, headers);
            if (response.status === 200) {
                const jsonResponse = (await response.json()) as unknown as TradeInAemVehicleTypeSw;
                return { results: [jsonResponse] };
            } else {
                return { results: [] };
            }
        };

        await handleVehicleFetch(dispatch, fetchFn);
    }

    async fetchVehicleQuote(): Promise<void> {
        const { dispatch, getState } = this.getStore();
        const { tradeInAemVehicle } = getState();
        const vehicle = tradeInAemVehicle.vehicle as TradeInAemVehicleTypeSw;

        if (vehicle.valuation) {
            // TSW is a special case where the quote is already part of the initial fetchVehicle response.
            // To maintain compatibility with the general trade-in flow and data structure we read the quote from the vehicle and set it in the quote reducer
            dispatch(tradeInAemSetQuote(vehicle.valuation));
        } else {
            dispatch(tradeInAemSetQuoteFailed(true));
        }
    }

    getEHubVehicleData(): TradeInAemEHubPayload["vehicle"] {
        const currentState = this.getStore().getState();
        const vehicleName = this.vehicleNameSelector(currentState);
        const { tradeInAemVehicle, tradeInAemVehicleDetails } = currentState;
        const vehicle = tradeInAemVehicle.vehicle as TradeInAemVehicleTypeSw;

        const quoteId = this.vehicleValueSelector(currentState)?.quoteId || null;

        return {
            quoteId,
            licensePlate: vehicle.plate,
            vin: vehicle.vin,
            name: `${vehicleName.name} - ${vehicleName.description || ""}`,
            manufacturer: vehicle.brand,
            model: vehicle.model,
            modelYear: String(vehicle.modelYear),
            bodyType: vehicle.chassis,
            mileage: (tradeInAemVehicleDetails.formItems?.mileage?.value as string) || null,
            engine: vehicle.engineName,
            transmission: null,
            doors: null,
            fuelType: null,
            co2: null,
            pdfLink: null, // TODO support PDF for TSW OR-8352
            registrationDate: null,
        };
    }

    // TODO test this output once the value call is available in the trade-in api
    getExtraVehicleDetailsPdf = (): TradeInAemSwExtraVehicleDetailsPdfType => {
        const currentState = this.getStore().getState();
        const { tradeInAemVehicle } = currentState;
        const vehicle = tradeInAemVehicle.vehicle as TradeInAemVehicleTypeSw;

        const typeOfGears = vehicle.attributes.find(({ id }) => id === "g7")?.attributes?.[0]?.name;
        const numberOfGears = vehicle.attributes.find(({ id }) => id === "g8")?.attributes?.[0]?.name;

        const baseObject: TradeInAemExtraVehicleDetailsPdfBaseType = {
            manufacturer: vehicle.brand,
            model: vehicle.model,
            grade: null,
            generation: vehicle.generation,
            engine: vehicle.engineName,
            powerHp: `${vehicle.horsepower}`,
            powerPs: vehicle.attributes.find(({ id }) => id === "151")?.values?.join(",") || null,
            powerKw: vehicle.attributes.find(({ id }) => id === "152")?.values?.join(",") || null,
            body: vehicle.attributes.find(({ id }) => id === "g42")?.attributes?.[0]?.name || null,
            doors: vehicle.attributes.find(({ id }) => id === "g91")?.attributes?.[0]?.name || null,
            fuel: vehicle.attributes.find(({ id }) => id === "g14")?.attributes?.[0]?.name || null,
            transmission: null,
            gears: `${typeOfGears || ""}${numberOfGears ? `(${numberOfGears})` : ""}`,
            colour: vehicle.attributes.find(({ id }) => id === "g13")?.attributes?.[0]?.name || null,
            regDate: null,
            vin: vehicle.vin,
            seats: null,
            wheeldrive: null,
            cc: null,
            year: null,
        };

        // TSW specific
        const csObject: TradeInAemExtraVehicleDetailsPdfSwSpecificType = {
            series: vehicle.series,
            chassis: vehicle.chassis,
            modelYear: vehicle.modelYear,
            engineStartYear: vehicle.engineStartYear,
            engineEndYear: vehicle.engineEndYear,
            trimPackage: vehicle.trimPackage,
            driveline: vehicle.attributes.find(({ id }) => id === "g97")?.attributes?.[0]?.name || null,
        };

        return { ...baseObject, ...csObject };
    };

    prefillFormsWithTestData(): void {
        super.prefillFormsWithTestData();

        // Prefill custom vehicle details values
        this.setFormItemValue("mileage", "100000");
    }
}
